function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from "react";
import Placeholder from "./Placeholder";
import { ALIGN, CSS_MEDIA, GAP_SIZES, MEDIA } from "../constants/LayoutConstants";
import clsx from 'clsx';
import '../css/grid.css';
import { Component } from 'react';

/**
 * @typedef {('center'|'top'|'bottom'|'stretch')} ALIGN
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/
 */

/**
 * @typedef {('auto'|'fr'|'px'|'minmax()'|'repeat()')} TEMPLATE
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/
 */

/**
 * @typedef {('left'|'right'|'center'|'space-between'|'space-around'|'space-evenly')} JUSTIFY
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/
 */

let _CONFIG = {
  //We use flexbox gap and grid gap by default, older browsers may not support these.
  //If true the library will use margins to simulate gaps
  useMarginGap: false,
  //Enabled borders around grid and flex child elements, useful when developing new layouts
  enableGridInspector: false,
  //Allow the use of custom css media breakpoints
  breakpoints: {}
};
export function setConfig(config) {
  _CONFIG = {
    ..._CONFIG,
    ...config
  };
}
function getConfig() {
  return _CONFIG;
}
const getViewport = props => {
  let size = props.xs || "";
  const childrenCount = React.Children.count(props.children);
  if (window.matchMedia(_CONFIG.breakpoints.PHONE || MEDIA.PHONE).matches) {
    size = props.xs || size;
  }
  if (window.matchMedia(_CONFIG.breakpoints.TABLET || MEDIA.TABLET).matches) {
    size = props.sm || props.xs || size;
  }
  if (window.matchMedia(_CONFIG.breakpoints.LAPTOP || MEDIA.LAPTOP).matches) {
    size = props.md || props.sm || props.xs || size;
  }
  if (window.matchMedia(_CONFIG.breakpoints.DESKTOP || MEDIA.DESKTOP).matches) {
    size = props.lg || props.md || props.sm || props.xs || size;
  }
  if (window.matchMedia(_CONFIG.breakpoints.WIDE || MEDIA.WIDE).matches) {
    size = props.xl || props.lg || props.md || props.sm || props.xs || size;
  }
  if (childrenCount === 1 && size) size = size.split(" ")[0];
  if (props.gridAutoFlow) return null;
  return size ? size.replace(/fit/g, "min-content") : getDefaultViewPort(childrenCount);
};
const getDefaultViewPort = children => {
  let str = "auto";
  for (let i = 1; i < children; i++) {
    str += " auto";
  }
  return str;
};
const replaceGap = (gapStr, requiredCount) => {
  const strArray = gapStr.split(" ");
  const result = [];
  if (requiredCount <= 1) {
    return [GAP_SIZES.none];
  }
  for (let i = 0; i < 2; i++) {
    const str = strArray[i];
    if (GAP_SIZES[str]) {
      result.push(GAP_SIZES[str]);
    }
  }
  return result.join(" ");
};
export const addMatchMediaEventListener = (str, callback) => {
  if (!matchMedia(str).addEventListener) {
    return matchMedia(str).addListener(callback);
  }
  return matchMedia(str).addEventListener("change", callback);
};
export const removeMatchMediaEventListener = (str, callback) => {
  if (!matchMedia(str).removeEventListener) {
    return matchMedia(str).removeListener(callback);
  }
  return matchMedia(str).removeEventListener("change", callback);
};
const getDeviceSize = props => {
  if (props.andAbove) {
    return CSS_MEDIA[`${props.size}_AND_ABOVE`];
  }
  if (props.andBelow) {
    return CSS_MEDIA[`${props.size}_AND_BELOW`];
  }
  return CSS_MEDIA[props.size];
};

/**
 * @component 
 * @description
 * returns which device width is currently in use
 *
 * @see GRID_REPO_URL - Documentation
 *
 * @extends {Component<OnDeviceProps>}
 * @returns {JSX.Element} JSX Element
 *
 */
class OnDevice extends Component {
  /**
  * @typedef {Object}     OnDeviceProps
  * @prop {MEDIA}         [size=desktop]       - The MEDIA size you'd like to check against.
  * @prop {boolean}       [andAbove=false]           - Include is bigger then size
  * @prop {boolean}       [andBelow=false]           - Include is smaller then size
  * @memberof OnDevice 
  * 
  */
  static defaultProps = {
    size: MEDIA.DESKTOP,
    andAbove: false,
    andBelow: false
  };
  state = {
    render: window.matchMedia(getDeviceSize(this.props)).matches
  };
  componentDidMount() {
    addMatchMediaEventListener(getDeviceSize(this.props), this.updateLayout);
  }
  updateLayout = () => {
    if (window.matchMedia(getDeviceSize(this.props)).matches) {
      return this.setState({
        render: true
      });
    }
    this.setState({
      render: false
    });
  };
  render() {
    if (this.state.render) return this.props.children;
    return null;
  }
}
export const isDeviceSize = size => {
  return window.matchMedia(size).matches;
};

/**
 * @class
 * @component
 * OnMobile
 * 
 * @description
 * Will only render children if the app viewport matches PHONE width
 * 
 * @example
 * <OnMobile>
 *      Your device is a PHONE!
 * </OnMobile>
 * 
 * @extends {Component<Props>}
 * @returns {JSX.Element}
 */
export const OnMobile = props => {
  return /*#__PURE__*/React.createElement(OnDevice, _extends({
    size: "PHONE"
  }, props));
};

/**
 * @class
 * @component
 * 
 * @description
 * Will only render children if the app viewport matches TABLET width
 * 
 * @extends {Component<Props>}
 * @returns {JSX.Element}
 * 
 * @example
 * <OnTablet>
 *      Your device is a TABLET!
 * </OnTablet>
 * 
 */
export const OnTablet = props => {
  return /*#__PURE__*/React.createElement(OnDevice, _extends({
    size: "TABLET"
  }, props));
};

/**
 * @class
 * @component
 * 
 * @description
 * Will only render children if the app viewport matches LAPTOP width
 * 
 * @extends {Component<Props>}
 * @returns {JSX.Element}
 * 
 * @example
 * <OnLaptop>
 *      Your device is a LAPTOP!
 * </OnLaptop>
 * 
 * 
 */
export const OnLaptop = props => {
  return /*#__PURE__*/React.createElement(OnDevice, _extends({
    size: "LAPTOP"
  }, props));
};

/**
 * @class
 * @component
 * 
 * @description
 * Will only render children if the app viewport matches DESKTOP width
 * 
 * @extends {Component<Props>}
 * @returns {JSX.Element}
 * 
 * @example
 * <OnDesktop>
 *      Your device is a DESKTOP!
 * </OnDesktop>
 * 
 */
export const OnDesktop = props => {
  return /*#__PURE__*/React.createElement(OnDevice, _extends({
    size: "DESKTOP"
  }, props));
};

/**
 * @class
 * @component
 * 
 * @description
 * Will only render children if the app viewport matches WIDE_DESKTOP width
 * 
 * @extends {Component<Props>}
 * @returns {JSX.Element}
 */
export const OnWide = props => {
  return /*#__PURE__*/React.createElement(OnDevice, _extends({
    size: "WIDE"
  }, props));
};

/**
 * @component 
 * @description
 * Horizontal layout component utilizing css grid.
 *
 * @see GRID_REPO_URL - Documentation
 *
 * @extends {Component<RowProps>}
 * @returns {JSX.Element} JSX Element
 *
 * @example
 * <Row gap="sm" align="center" justify="center" xs="auto 1fr auto">
 *      <IconButton/>
 *      <h2>Title</h2>
 *      <IconButton/>
 * </Row>
 */
export class Row extends Component {
  /**
  * @typedef {Object}     RowProps
  * @prop {GAP_SIZES}     [gap=none]              - Adds spacing between children
  * @prop {boolean}       [noWrap=false]           - Will prevent children from wrapping
  * @prop {GAP_SIZES}     [rowGap=none]           - Adds horizontal spacing between children 
  * @prop {GAP_SIZES}     [columnGap=none]        - Adds vertical spacing between children
  * @prop {boolean}       [column=false]           - Vertical layout for children
  * @prop {ALIGN}         [align=null]            - Align children according to value
  * @prop {JUSTIFY}       [justify=null]          - Justify children according to value
  * @prop {boolean}       [placeholder=false]      - Will inject a colorful placeholder for content
  * @prop {boolean}       [scroll=false]           - Will apply scrolling when overflow occurs
  * @prop {boolean}       [fill=false]             - Matches the width of the container element
  * @prop {boolean}       [showGrid=false]         - Shows borders in UI to preview grids
  * @prop {TEMPLATE}      [xs=auto]               - Applies children layout when screen width matches PHONE size
  * @prop {TEMPLATE}      [sm=none]               - Applies children layout when screen width matches TABLET size
  * @prop {TEMPLATE}      [md=none]               - Applies children layout when screen width matches LAPTOP size
  * @prop {TEMPLATE}      [lg=none]               - Applies children layout when screen width matches DESKTOP size
  * @prop {TEMPLATE}      [xl=none]               - Applies children layout when screen width matches WIDE_DESKTOP size
  * @memberof Row 
  * 
  */
  static defaultProps = {
    rows: null,
    gap: null,
    noWrap: false,
    rowGap: null,
    columnGap: null,
    column: false,
    gridAutoRows: null,
    gridAutoColumns: null,
    align: null,
    alignItems: null,
    justify: null,
    justifyItems: null,
    placeholder: null,
    scroll: false,
    fill: false,
    showGrid: getConfig().enableGridInspector,
    xs: null,
    sm: null,
    md: null,
    lg: null,
    xl: null
  };
  constructor(props) {
    super(props);
    this.gridTemplate = getViewport(props);
    this.el = /*#__PURE__*/React.createRef();
  }
  componentDidMount() {
    addMatchMediaEventListener(_CONFIG.breakpoints.PHONE || "(max-width: 425px)", this.updateLayout);
    addMatchMediaEventListener(_CONFIG.breakpoints.TABLET || "(max-width: 768px)", this.updateLayout);
    addMatchMediaEventListener(_CONFIG.breakpoints.LAPTOP || "(max-width: 960px)", this.updateLayout);
    addMatchMediaEventListener(_CONFIG.breakpoints.DESKTOP || "(max-width: 1024px)", this.updateLayout);
    addMatchMediaEventListener(_CONFIG.breakpoints.WIDE || "(max-width: 1440px)", this.updateLayout);
    //Css grid content requires if you want to scroll inside you need to put minHeight = 0 on the grids parent element.
    //TODO: Add checks to see if a user has set the minheight if they have show  warning that we need it to be minHeight 0;
    if (this.props.scroll) {
      const parentGrid = this.el.current.parentNode;
      parentGrid.style.minHeight = "0";
      parentGrid.style.position = "relative";
    }
  }
  componentWillUnmount() {
    removeMatchMediaEventListener(_CONFIG.breakpoints.PHONE || "(max-width: 425px)", this.updateLayout);
    removeMatchMediaEventListener(_CONFIG.breakpoints.TABLET || "(max-width: 768px)", this.updateLayout);
    removeMatchMediaEventListener(_CONFIG.breakpoints.LAPTOP || "(max-width: 960px)", this.updateLayout);
    removeMatchMediaEventListener(_CONFIG.breakpoints.DESKTOP || "(max-width: 1024px)", this.updateLayout);
    removeMatchMediaEventListener(_CONFIG.breakpoints.WIDE || "(max-width: 1440px)", this.updateLayout);
  }
  componentDidUpdate(prevProps, prevState) {
    this.updateLayout();
  }
  updateLayout = () => {
    this.gridTemplate = getViewport(this.props);
    if (!this.el.current || this.props.gridAutoColumns || this.props.gridAutoRows) return;
    if (!this.props.column) this.el.current.style.gridTemplateColumns = this.gridTemplate;else {
      this.el.current.style.gridTemplateRows = this.gridTemplate;
    }
  };
  getJustify() {
    const {
      justify,
      justifyItems,
      center
    } = this.props;
    if (center) return "center";
    return ALIGN[justify || justifyItems];
  }
  getAlign() {
    const {
      align,
      alignItems,
      center
    } = this.props;
    if (center) return "center";
    return ALIGN[align || alignItems];
  }
  getAutoFlow() {
    const {
      column,
      xs,
      sm,
      md,
      lg,
      xl,
      gridAutoFlow
    } = this.props;
    if (gridAutoFlow) return gridAutoFlow;
    if (xs || sm || md || lg || xl) return null;
    if (column) return "row";
    return "column";
  }
  render() {
    const {
      columnCount,
      gap,
      rows,
      alignItems,
      noWrap,
      columnGap,
      gridAutoRows,
      gridAutoColumns,
      justify,
      justifyItems,
      showGrid,
      align,
      center,
      gridAutoFlow,
      placeholder,
      scroll,
      rowGap,
      xs,
      sm,
      md,
      lg,
      xl,
      column,
      fill,
      ...other
    } = this.props;
    const gridInspector = showGrid || _CONFIG.enableGridInspector;
    const autoFlow = this.getAutoFlow();
    let gridTemplate = !autoFlow && !gridAutoColumns ? this.gridTemplate : "";
    const childCount = React.Children.count(other.children);
    if (childCount === 1) {
      gridTemplate = gridTemplate.split(" ")[0];
    }
    const style = {
      alignContent: align || center ? this.getAlign() : null,
      justifyContent: justify || center ? this.getJustify() : null,
      alignItems: alignItems || align || center ? this.getAlign() : null,
      justifyItems: justifyItems || center ? this.getJustify() : null,
      display: "grid",
      whiteSpace: noWrap ? "nowrap" : "normal",
      //rowGap: GAP_SIZES[rowGap || gap],
      //columnGap: GAP_SIZES[columnGap || gap],
      width: fill ? "100%" : "auto",
      gap: gap ? replaceGap(gap, childCount) : "",
      overflow: scroll ? "auto" : null,
      gridAutoFlow: autoFlow,
      gridAutoColumns: gridAutoColumns,
      gridTemplateColumns: column && !rows || gridAutoColumns ? null : gridTemplate,
      gridTemplateRows: column ? gridTemplate : rows ? rows : null,
      ...other.style
    };
    if (fill) {
      style.height = "100%";
    }
    if (rowGap) style.rowGap = GAP_SIZES[rowGap];
    if (columnGap) style.columnGap = GAP_SIZES[columnGap];
    if (gridAutoRows) style.gridAutoRows = "max-content";
    return /*#__PURE__*/React.createElement("div", _extends({}, other, {
      className: clsx("grid-row", other.className, {
        "show-grid": gridInspector
      }),
      ref: this.el,
      style: style
    }), placeholder && !other.children ? /*#__PURE__*/React.createElement(Placeholder, null, placeholder) : other.children);
  }
}
const defaultFlex = "0 0 auto";

/**

 * @component

 * @description
 * Vertical layout component utilizing Flexbox.
 * This entire component exists because Iphone and safari have severe performance drops
 * from nested display:grid elements. So instead of rewriting all our apps layout
 * I created a Column component that uses flex-box instead. After testing it does resolve these
 * issues, however it does behave slightly differently then a grid based column. 
 *
 * @see GRID_REPO_URL - Documentation
 * @extends {Component<ColumnProps>}
 * @returns {JSX.Element} JSX Element
 * 
 * @example
 * <Column gap="sm" xs="auto 1fr">
 *      <header>
 *          <h2>Title</h2>
 *      </header>
 *      <div className="content-wrapper scroll">
 *          <p>Content</p>
 *      </div>
 * </Row>
 *
 */
export class Column extends Component {
  /**
  * @typedef {Object}     ColumnProps
  * @prop {GAP_SIZES}     [gap=none]              - Adds spacing between children
  * @prop {boolean}       [noWrap=false]           - Will prevent children from wrapping
  * @prop {ALIGN}         [align=null]            - Align children according to value
  * @prop {JUSTIFY}       [justify=null]          - Justify children according to value
  * @prop {boolean}       [scroll=false]           - Will apply scrolling when overflow occurs
  * @prop {boolean}       [fill=false]             - Matches the width of the container element
  * @prop {boolean}       [showGrid=false]         - Shows borders in UI to preview grids
  * @prop {TEMPLATE}      [xs=auto]               - Flexbox sizing for filling space
   * @memberof Column
   */
  static defaultProps = {
    xs: null,
    scroll: false,
    gap: null,
    align: null,
    justify: null,
    fill: false,
    center: false,
    noWrap: false,
    heightOffset: 0,
    showGrid: false
  };
  constructor(props) {
    super(props);
    this.el = /*#__PURE__*/React.createRef();
  }
  componentDidMount() {
    const {
      scroll,
      heightOffset
    } = this.props;
    if (scroll) {
      const offsetTop = this.el.current.offsetTop;
      const height = window.innerHeight - offsetTop + heightOffset;
      this.el.current.style.height = height + "px";
    }
  }
  updateLayout = () => {
    if (!this.el.current) return;
    var children = this.el.current.children;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      child.style.flex = this.getChildStylesByPosition(i).flex;
    }
  };
  getViewport() {
    const props = this.props;
    let size = props.xs || "";
    if (window.matchMedia(_CONFIG.breakpoints.PHONE || MEDIA.PHONE).matches) {
      size = props.xs || size;
    }
    if (window.matchMedia(_CONFIG.breakpoints.TABLET || MEDIA.TABLET).matches) {
      size = props.sm || props.xs || size;
    }
    if (window.matchMedia(_CONFIG.breakpoints.LAPTOP || MEDIA.LAPTOP).matches) {
      size = props.md || props.sm || props.xs || size;
    }
    if (window.matchMedia(_CONFIG.breakpoints.DESKTOP || MEDIA.DESKTOP).matches) {
      size = props.lg || props.md || props.sm || props.xs || size;
    }
    if (window.matchMedia(_CONFIG.breakpoints.WIDE || MEDIA.WIDE).matches) {
      size = props.xl || props.lg || props.md || props.sm || props.xs || size;
    }
    return size;
  }
  getChildStylesByPosition(index) {
    const layout = this.getViewport();
    if (!layout) return {
      flex: defaultFlex
    };
    const layoutArray = layout ? layout.split(' ') : [];
    if (index >= layoutArray.length) {
      return {
        flex: defaultFlex
      };
    }
    const layoutValue = layoutArray[index];
    const valueNumber = parseInt(layoutValue, 10);
    if (!isNaN(valueNumber)) {
      if (layoutValue.indexOf("fr") >= 0) {
        return {
          flex: `${valueNumber} 1 auto`
        };
      }
      return {
        flex: `0 0 ${layoutValue}`
      };
    }
    return {
      flex: defaultFlex
    };
  }
  renderChildren(props, el, className, gridInspector, rowStyle, children) {
    return /*#__PURE__*/React.createElement("div", _extends({}, props, {
      ref: el,
      className: clsx("flex-column", className, {
        "show-grid": gridInspector
      }),
      style: rowStyle
    }), children);
  }
  render() {
    const {
      gap,
      scroll,
      showGrid,
      heightOffset,
      children,
      center,
      align,
      justify,
      noWrap,
      fill,
      ...other
    } = this.props;
    const count = React.Children.count(children);
    const gridInspector = showGrid || _CONFIG.enableGridInspector;
    let rowStyle = {
      ...this.props.style,
      display: 'flex',
      flexDirection: 'column',
      minWidth: '0',
      whiteSpace: 'normal',
      width: "100%"
    };
    if (align) rowStyle.alignItems = ALIGN[align];
    if (justify) {
      rowStyle.justifyContent = ALIGN[justify];
    }
    ;
    if (center) {
      rowStyle.alignItems = "center";
      rowStyle.justifyContent = "center";
    }
    ;
    if (fill) {
      rowStyle.height = "100%";
    }
    ;
    if (noWrap) {
      rowStyle.whiteSpace = "nowrap";
    }
    ;
    if (!_CONFIG.useMarginGap) {
      rowStyle.gap = GAP_SIZES[gap];
    }
    if (scroll) {
      rowStyle.overflow = "auto";
    }
    let finalChildren = [];
    React.Children.forEach(children, (child, i) => {
      if (!child) return;
      let childStyle = {
        //minWidth:"min-content",
        ...this.getChildStylesByPosition(i)
      };
      if (_CONFIG.useMarginGap && gap && i < count - 1) {
        childStyle.marginBottom = GAP_SIZES[gap];
      }
      if (!child) return;
      finalChildren.push(/*#__PURE__*/React.cloneElement(child, {
        key: child.key || i,
        style: {
          ...child.props.style,
          ...childStyle
        }
      }));
    });
    return this.renderChildren(other, this.el, this.props.className, gridInspector, rowStyle, finalChildren);
  }
}
;